import "./lib/instrument";

import { ReactNode, StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { AuthComplete } from "./pages/AuthComplete";
import { Settings } from "./pages/Settings";
import Teams from "./pages/Teams";
import AllAgents from "./pages/AllAgents";
import AgentDetailsPage from "./pages/AgentDetails";
import StandardAgent from "./pages/StandardAgent";
import Inbox from "./pages/Inbox";
import Onboarding from "./pages/Onboarding";
import AnalyticsPage from "./pages/Analytics";
import Debug from "./pages/Debug";
import AgentPulsePage from "./pages/AgentPulse";
import { useAuthStore } from "./store/authStore";
import { initTheme } from "./store/themeStore";
import { useOrganizationStore } from "./store/organizationStore";
import { useFeatureFlag, useFeatureFlagStore } from "./store/featureFlagStore";
import { Toaster } from "./components/ui/sonner";
import { HomeRedirect } from "./pages/HomeRedirect";
import { PostHogProvider } from "posthog-js/react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "./lib/errors";

// Records pages
import Items from "./pages/records/Items";
import Operations from "./pages/records/Operations";
import Companies from "./pages/records/Companies";
import PriceLists from "./pages/records/PriceLists";
import People from "./pages/records/People";
import Files from "./pages/records/Files";
import RecordQuotes from "./pages/records/RecordQuotes";
import Invoices from "./pages/records/Invoices";
import Opportunities from "./pages/records/Opportunities";
import Locations from "./pages/records/Locations";
import Forecast from "./pages/Forecast";
import AgentRules from "./pages/AgentRules";
import DatasetExplorer from "./pages/records/DatasetExplorer";

// Public pages
import { PublicQuote } from "./pages/PublicQuote";

// OAuth callback pages
import { OAuthCallback } from "./pages/OAuthCallback";

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
};

// Create an AppInitializer component to use hooks (can't use hooks at the top level)
function AppInitializer({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Initialize auth state when the app starts
    useAuthStore.getState().initializeAuth();

    // Initialize organization data when the user is available
    const initializeOrganization = () => {
      if (useAuthStore.getState().user) {
        useOrganizationStore.getState().fetchOrganization();
        useFeatureFlagStore.getState().fetchFlags();
      }
    };

    // Call initializations
    initializeOrganization();

    // Set up a listener for auth state changes
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.user) {
        useOrganizationStore.getState().fetchOrganization();
        useFeatureFlagStore.getState().fetchFlags();
      } else {
        useFeatureFlagStore.getState().reset();
      }
    });

    return () => {
      unsubscribe(); // Cleanup subscription on unmount
    };
  }, []);

  return <>{children}</>;
}

const AppContent = () => {
  const demandForecastingEnabled = useFeatureFlag("demand-forecasting");
  const authLoading = useAuthStore((state) => state.isLoading);
  const hasUser = useAuthStore((state) => Boolean(state.user));
  const featureFlagsLoaded = useFeatureFlagStore((state) => state.loaded);
  const featureFlagsLoading = useFeatureFlagStore((state) => state.loading);
  const demandForecastingFlagPending =
    authLoading || (hasUser && !featureFlagsLoaded && featureFlagsLoading);

  return (
    <TooltipProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/auth/complete" element={<AuthComplete />} />
          <Route path="/auth/:provider/callback" element={<OAuthCallback />} />
          <Route path="/agents" element={<AllAgents />} />
          <Route path="/agents/:agentId" element={<AgentDetailsPage />} />
          <Route path="/standard-agent" element={<StandardAgent />} />
          <Route path="/inbox" element={<Inbox />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route
            path="/forecast"
            element={
              demandForecastingFlagPending ? null : demandForecastingEnabled ? (
                <Forecast />
              ) : (
                <Navigate to="/inbox" replace />
              )
            }
          />
          <Route path="/agent-pulse" element={<AgentPulsePage />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/debug" element={<Debug />} />

          {/* Records Routes */}
          <Route path="/items" element={<Items />} />
          <Route path="/operations" element={<Operations />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/companies/:companyId" element={<Companies />} />
          <Route path="/price-lists" element={<PriceLists />} />
          <Route path="/people" element={<People />} />
          <Route path="/people/:personId" element={<People />} />
          <Route
            path="/knowledgebases"
            element={<Navigate to="/vault" replace />}
          />
          <Route path="/vault" element={<Files />} />
          <Route path="/files" element={<Navigate to="/vault" replace />} />
          <Route path="/quotes" element={<RecordQuotes />} />
          <Route path="/opportunities" element={<Opportunities />} />
          <Route
            path="/opportunities/:opportunityId"
            element={<Opportunities />}
          />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/shop-rules" element={<AgentRules />} />
          <Route path="/dataset-explorer" element={<DatasetExplorer />} />

          {/* Public Routes */}
          <Route path="/public/quotes/:quoteId" element={<PublicQuote />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </TooltipProvider>
  );
};

initTheme();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <AppInitializer>
        {import.meta.env.VITE_PUBLIC_POSTHOG_KEY ? (
          <PostHogProvider
            apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
            options={options}
          >
            <AppContent />
          </PostHogProvider>
        ) : (
          <AppContent />
        )}
      </AppInitializer>
    </ErrorBoundary>
  </StrictMode>,
);
